
import { Roles } from "@/domains/shared/models/roles";
import { isFreePlan, normalizePlanSlug, SubscriptionPlanIds } from "@/domains/subscription/plans";
import { CLINIC_HOME_PATH, CLINIC_SUBSCRIPTION_PATH } from "@/domains/multi/clinicMemberPaths";

export const AccountAccessModes = {
    active: "active",
    deactivatedBillingOnly: "deactivated_billing_only",
    deactivatedClosed: "deactivated_closed",
} as const;

export type AccountAccessMode = (typeof AccountAccessModes)[keyof typeof AccountAccessModes];

export const THERAPIST_BILLING_SETTINGS_PATH = "/profissional/configuracoes/cobranca";
export const THERAPIST_SETTINGS_PATH = "/profissional/configuracoes";
export const THERAPIST_SETTINGS_AGENDA_PATH = "/profissional/configuracoes/agenda";
export const THERAPIST_PROFILE_PATH = "/profissional/perfil";
export const THERAPIST_PRIVACY_PATH = "/profissional/privacidade";
export const THERAPIST_PROFILE_ADDRESS_HASH = "#profile-address-postalCode";
export const THERAPIST_PROFILE_PHONE_HASH = "#profile-phone-input";
export const THERAPIST_PROFILE_CPF_HASH = "#profile-cpf-input";

/** Routes reachable while Pro signup payment is still pending (checkout + address for card). */
export function isTherapistPaymentSetupPath(pathname: string): boolean {
    const normalized = pathname.replace(/\/+$/, "");
    return normalized === CLINIC_SUBSCRIPTION_PATH
        || normalized === THERAPIST_PROFILE_PATH
        || normalized === THERAPIST_SETTINGS_PATH
        || normalized === THERAPIST_BILLING_SETTINGS_PATH;
}

/** Profissional pages still reachable while clinical access is blocked (Perfil + Assinatura clínica). */
export function isTherapistBlockedShellPath(pathname: string): boolean {
    const normalized = pathname.replace(/\/+$/, "");
    return normalized === CLINIC_SUBSCRIPTION_PATH
        || normalized === THERAPIST_PROFILE_PATH;
}

/** Auto-open pending checkout only on Assinatura so profile/settings stay usable for address. */
export function shouldAutoOpenPendingCheckout(pathname: string): boolean {
    const normalized = pathname.replace(/\/+$/, "");
    return normalized === CLINIC_SUBSCRIPTION_PATH;
}
export const THERAPIST_TRIAL_REACTIVATION_PATH = "/reativar-conta";
export const THERAPIST_TRIAL_PAYWALL_PATH = "/reativar-conta/plano";

export function isBillingOnlyAccess(mode?: string | null): boolean {
    return mode === AccountAccessModes.deactivatedBillingOnly;
}

export type ClinicalAccessUser = {
    role?: string;
    clinicalAccessBlocked?: boolean | null;
    clinicalAccessBlockedReason?: string | null;
    accountAccessMode?: string | null;
    subscriptionPlan?: string | null;
    subscriptionEndsAt?: string | null;
    trialDaysRemaining?: number | null;
    activeClinicId?: string | null;
    referralCheckoutPending?: boolean | null;
    secretaryAccessBlocked?: boolean | null;
    clinic?: { clinicalAccessBlocked?: boolean | null; subscriptionStatus?: string | null } | null;
};

/** True when the therapist's subscription/grace period timestamp has passed. */
export function isSubscriptionPeriodEnded(subscriptionEndsAt?: string | null, nowMs: number = Date.now()): boolean {
    const trimmed = subscriptionEndsAt?.trim();
    if (!trimmed) {
        return false;
    }

    const endMs = Date.parse(trimmed);
    if (Number.isNaN(endMs)) {
        return false;
    }

    return nowMs > endMs;
}

/** Later of two ISO timestamps. Omits null/invalid; does not invent a third date. */
export function laterInstantIso(a?: string | null, b?: string | null): string | null {
    const aMs = a?.trim() ? Date.parse(a) : Number.NaN;
    const bMs = b?.trim() ? Date.parse(b) : Number.NaN;
    const aOk = !Number.isNaN(aMs);
    const bOk = !Number.isNaN(bMs);
    if (aOk && bOk) {
        return aMs >= bMs ? a!.trim() : b!.trim();
    }

    if (aOk) {
        return a!.trim();
    }

    if (bOk) {
        return b!.trim();
    }

    return null;
}

function isBasicPlanSlug(plan?: string | null): boolean {
    const normalized = plan?.trim();
    return normalized === SubscriptionPlanIds.basic
        || normalized === "Basico"
        || normalized === "Básico";
}

/** Derives solo-therapist clinical block from /auth/me when the API flag is absent or stale. */
export function isSoloTherapistClinicalAccessBlockedBySubscription(
    user?: ClinicalAccessUser | null,
    nowMs: number = Date.now(),
): boolean {
    if (!user || user.role !== Roles.Therapist) {
        return false;
    }

    if (user.activeClinicId) {
        return false;
    }

    if (isFreePlan(user.subscriptionPlan)) {
        return false;
    }

    if (user.trialDaysRemaining === 0) {
        return true;
    }

    return isSubscriptionPeriodEnded(user.subscriptionEndsAt, nowMs);
}

export function isClinicalAccessBlocked(user?: ClinicalAccessUser | null): boolean {
    if (!user) {
        return false;
    }

    if (user.clinicalAccessBlocked === true || user.clinic?.clinicalAccessBlocked === true) {
        return true;
    }

    if (user.role === Roles.Secretary && user.secretaryAccessBlocked === true) {
        return true;
    }

    if (isBillingOnlyAccess(user.accountAccessMode)) {
        return true;
    }

    return isSoloTherapistClinicalAccessBlockedBySubscription(user);
}

export function resolveClinicalAccessBlockedReason(user?: ClinicalAccessUser | null): string | null {
    if (user?.clinicalAccessBlockedReason) {
        return user.clinicalAccessBlockedReason;
    }

    if (!isClinicalAccessBlocked(user)) {
        return null;
    }

    if (isBillingOnlyAccess(user?.accountAccessMode)) {
        return "billing_only";
    }

    if (user?.clinic?.subscriptionStatus === "past_due"
        || user?.clinic?.subscriptionStatus === "expired") {
        return "org_billing_blocked";
    }

    if (isBasicPlanSlug(user?.subscriptionPlan)) {
        return "trial_expired";
    }

    return "payment_required";
}

export const SECRETARY_HOME_PATH = "/administrativo";
export const SECRETARY_PROFILE_PATH = "/administrativo/perfil";

export function resolveClinicalAccessRedirectPath(user?: ClinicalAccessUser | null): string {
    const reason = resolveClinicalAccessBlockedReason(user);
    if (user?.role === Roles.Secretary) {
        return SECRETARY_HOME_PATH;
    }

    if (reason === "org_billing_blocked") {
        return CLINIC_SUBSCRIPTION_PATH;
    }

    return CLINIC_SUBSCRIPTION_PATH;
}

type ClinicalAccessNavUser = ClinicalAccessUser;

/** Nav destinations still reachable while clinical access is blocked. */
export function isNavPathAllowedWhenClinicalAccessBlocked(
    pathname: string,
    user?: ClinicalAccessNavUser | null,
): boolean {
    const normalized = pathname.replace(/\/+$/, "");
    const reason = resolveClinicalAccessBlockedReason(user);

    if (user?.role === Roles.Secretary) {
        return normalized === SECRETARY_HOME_PATH || normalized === SECRETARY_PROFILE_PATH;
    }

    if (normalized === CLINIC_HOME_PATH
        || normalized === CLINIC_SUBSCRIPTION_PATH) {
        return true;
    }

    return isTherapistBlockedShellPath(pathname);
}

export function isNavItemLockedForClinicalAccess(
    navPath: string,
    user?: ClinicalAccessNavUser | null,
): boolean {
    if (!user || (user.role !== Roles.Therapist && user.role !== Roles.Secretary)) {
        return false;
    }

    if (!isClinicalAccessBlocked(user)) {
        return false;
    }

    return !isNavPathAllowedWhenClinicalAccessBlocked(navPath, user);
}

export function canUseClinicalFeatures(mode?: string | null): boolean {
    return !mode || mode === AccountAccessModes.active;
}


export type AssinaturaConversionInput = {
    role?: string | null;
    subscriptionPlan?: string | null;
    pendingPaidPlanId?: string | null;
    activeClinicId?: string | null;
    referralCheckoutPending?: boolean | null;
    billingStatus?: string | null;
    hasClinicalGrantingBilling?: boolean;
    trialDaysRemaining?: number | null;
    subscriptionEndsAt?: string | null;
};

/**
 * Solo titular in billing-only conversion (expired Básico trial or unpaid Pro,
 * no clinical-granting billing). Dual CTA on Assinatura only (BR-SAE-46–47).
 */
export function isAssinaturaConversionState(user?: AssinaturaConversionInput | null): boolean {
    if (!user || user.role !== Roles.Therapist) {
        return false;
    }

    // ClinicSync sets LastActiveClinicId on every therapist (tenancy, not org billing).
    // Clínica org uses the Clinic plan slug (BR-SAE-47).

    if (isFreePlan(user.subscriptionPlan)) {
        return false;
    }

    const slug = normalizePlanSlug(user.subscriptionPlan);
    if (slug !== SubscriptionPlanIds.basic && slug !== SubscriptionPlanIds.pro) {
        return false;
    }

    if (user.pendingPaidPlanId?.trim()) {
        return false;
    }

    if (user.hasClinicalGrantingBilling) {
        return false;
    }

    if (user.billingStatus === "past_due") {
        return false;
    }

    const hasOpenTrialWindow =
        (user.trialDaysRemaining != null && user.trialDaysRemaining > 0)
        || (Boolean(user.subscriptionEndsAt) && !isSubscriptionPeriodEnded(user.subscriptionEndsAt));

    return !hasOpenTrialWindow;
}

export function requiresPaymentBeforeAccess(user?: {
    role?: string;
    subscriptionPlan?: string | null;
    pendingPaidPlanId?: string | null;
    referralCheckoutPending?: boolean | null;
} | null): boolean {
    if (!user || user.role !== Roles.Therapist) {
        return false;
    }

    const pending = user.pendingPaidPlanId?.trim();
    if (!pending || pending !== "Pro") {
        return false;
    }

    const plan = user.subscriptionPlan ?? SubscriptionPlanIds.basic;
    return plan === SubscriptionPlanIds.basic || plan === "Básico" || plan === "Basico";
}
